import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";

const MONTHS = ["Січ", "Лют", "Бер", "Кві", "Тра", "Чер", "Лип", "Сер", "Вер", "Жов", "Лис", "Гру"];
const WD = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];
const pad = (x) => String(x).padStart(2, "0");

export default function PeriodPicker({ value, onChange, dayOnly = false, hideToggle = false }) {
  const { mode, date } = value;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(new Date(date));
  const ref = useRef(null);

  useEffect(() => {
    setView(new Date(date));
  }, [date, mode, open]);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const label =
    mode === "day"
      ? `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())}`
      : mode === "month"
      ? `${date.getFullYear()}/${pad(date.getMonth() + 1)}`
      : `${date.getFullYear()}`;

  const setMode = (m) => onChange({ mode: m, date });
  const step = (dir) => {
    const d = new Date(date);
    if (mode === "day") d.setDate(d.getDate() + dir);
    else if (mode === "month") d.setMonth(d.getMonth() + dir);
    else d.setFullYear(d.getFullYear() + dir);
    onChange({ mode, date: d });
  };
  const pick = (d) => {
    onChange({ mode, date: d });
    setOpen(false);
  };

  const navBtn = "p-1 text-slate-400 transition-colors hover:text-slate-700";

  const renderDay = () => {
    const y = view.getFullYear();
    const m = view.getMonth();
    const startWd = (new Date(y, m, 1).getDay() + 6) % 7;
    const days = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWd; i++) cells.push(null);
    for (let dn = 1; dn <= days; dn++) cells.push(dn);
    return (
      <div className="w-64 p-3">
        <div className="mb-2 flex items-center justify-between">
          <button className={navBtn} onClick={() => setView(new Date(y, m - 1, 1))}><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold text-slate-700">{y}/{pad(m + 1)}</span>
          <button className={navBtn} onClick={() => setView(new Date(y, m + 1, 1))}><ChevronRight size={16} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-slate-400">
          {WD.map((w) => <div key={w}>{w}</div>)}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-1">
          {cells.map((c, i) =>
            c === null ? (
              <div key={i} />
            ) : (
              <button
                key={i}
                onClick={() => pick(new Date(y, m, c))}
                className={`h-7 rounded text-xs transition-colors ${
                  date.getFullYear() === y && date.getMonth() === m && date.getDate() === c
                    ? "bg-blue-600 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {c}
              </button>
            )
          )}
        </div>
      </div>
    );
  };

  const renderMonth = () => {
    const y = view.getFullYear();
    return (
      <div className="w-64 p-3">
        <div className="mb-2 flex items-center justify-between">
          <button className={navBtn} onClick={() => setView(new Date(y - 1, 0, 1))}><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold text-slate-700">{y}</span>
          <button className={navBtn} onClick={() => setView(new Date(y + 1, 0, 1))}><ChevronRight size={16} /></button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {MONTHS.map((mn, i) => (
            <button
              key={mn}
              onClick={() => pick(new Date(y, i, 1))}
              className={`rounded py-2 text-sm transition-colors ${
                date.getFullYear() === y && date.getMonth() === i
                  ? "bg-blue-600 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {mn}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderYear = () => {
    const base = Math.floor(view.getFullYear() / 12) * 12;
    const years = Array.from({ length: 12 }, (_, i) => base + i);
    return (
      <div className="w-64 p-3">
        <div className="mb-2 flex items-center justify-between">
          <button className={navBtn} onClick={() => setView(new Date(base - 1, 0, 1))}><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold text-slate-700">{base}–{base + 11}</span>
          <button className={navBtn} onClick={() => setView(new Date(base + 12, 0, 1))}><ChevronRight size={16} /></button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {years.map((yr) => (
            <button
              key={yr}
              onClick={() => pick(new Date(yr, date.getMonth(), 1))}
              className={`rounded py-2 text-sm transition-colors ${
                date.getFullYear() === yr ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {yr}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex items-center gap-2" ref={ref}>
      {!dayOnly && !hideToggle && (
        <div className="flex overflow-hidden rounded-md border border-slate-200" data-testid="period-toggle">
          {[["day", "Д"], ["month", "М"], ["year", "Р"]].map(([m, l]) => (
            <button
              key={m}
              data-testid={`period-${m}`}
              onClick={() => setMode(m)}
              className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${
                mode === m ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-50"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      )}
      <div className="relative">
        <div className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600">
          <button className="text-slate-400 hover:text-slate-700" data-testid="period-prev" onClick={() => step(-1)}>
            <ChevronLeft size={14} />
          </button>
          <button className="flex items-center gap-1 font-mono" data-testid="period-label" onClick={() => setOpen((o) => !o)}>
            {label} <Calendar size={12} />
          </button>
          <button className="text-slate-400 hover:text-slate-700" data-testid="period-next" onClick={() => step(1)}>
            <ChevronRight size={14} />
          </button>
        </div>
        {open && (
          <div
            className="absolute right-0 z-30 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg"
            data-testid="period-popover"
          >
            {mode === "day" ? renderDay() : mode === "month" ? renderMonth() : renderYear()}
          </div>
        )}
      </div>
    </div>
  );
}
